/**
 * Layout building blocks of the Mediathek editors (EditMediaItem / EditMediaSeries):
 * header, main card with hairline-separated sections, side panels, segmented
 * controls, sticky action bar and the dirty-state / keyboard hooks.
 *
 * Everything renders inside <EditorShell>, which scopes MediathekEditor.scss.
 */
import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { MDBBtn, MDBIcon, MDBSpinner } from 'mdb-react-ui-kit';
import ReactCountryFlag from 'react-country-flag';
import { useTranslation } from 'react-i18next';
import { countChars, stableStringify } from '../../utils/mediathek';
import './MediathekEditor.scss';

const LANGUAGES = [
  { code: 'de', flag: 'DE', short: 'DE', labelKey: 'German' },
  { code: 'en', flag: 'GB', short: 'EN', labelKey: 'English' },
];

/** Icon prop: a Font Awesome solid icon name, or any React node. */
function renderIcon(icon) {
  if (!icon) return null;
  if (typeof icon === 'string') return <MDBIcon fas icon={icon} />;
  return icon;
}

function prefersReducedMotion() {
  try {
    return Boolean(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  } catch (e) {
    return false;
  }
}

/**
 * Scrolls an element (or the element with that id) into view - smooth unless the
 * user prefers reduced motion - and moves keyboard focus to it.
 * @param {string|HTMLElement} target
 * @returns {boolean} false when the element does not exist
 */
export function revealElement(target) {
  const element = typeof target === 'string' ? document.getElementById(target) : target;
  if (!element) return false;
  element.scrollIntoView({ behavior: prefersReducedMotion() ? 'auto' : 'smooth', block: 'start' });
  if (!element.hasAttribute('tabindex') && !/^(A|BUTTON|INPUT|SELECT|TEXTAREA)$/.test(element.tagName)) {
    element.setAttribute('tabindex', '-1');
  }
  element.focus({ preventScroll: true });
  return true;
}

/** Root of an editor page; scopes the stylesheet (.mediathek-editor). */
export function EditorShell({ className = '', children, ...rest }) {
  return (
    <div className={`mediathek-editor ${className}`.trim()} {...rest}>
      {children}
    </div>
  );
}

/**
 * Page header: thumbnail, eyebrow line, title (max. 2 lines), meta line
 * (each entry its own span, no separators) and status / actions on the right.
 */
export function EditorHeader({
  thumbnailUrl,
  thumbnailAlt = '',
  thumbnailIcon = 'photo-video',
  eyebrow,
  title,
  meta = [],
  status,
  actions,
  className = '',
}) {
  const [failedUrl, setFailedUrl] = useState(null);
  const showImage = Boolean(thumbnailUrl) && failedUrl !== thumbnailUrl;
  const metaItems = (Array.isArray(meta) ? meta : [meta]).filter((entry) => entry !== null && entry !== undefined && entry !== false && entry !== '');
  return (
    <header className={`card me-header ${className}`.trim()}>
      <div className="me-header-thumb">
        {showImage ? (
          <img src={thumbnailUrl} alt={thumbnailAlt} onError={() => setFailedUrl(thumbnailUrl)} />
        ) : (
          <span aria-hidden="true">{renderIcon(thumbnailIcon)}</span>
        )}
      </div>
      <div className="me-header-body">
        {eyebrow && <div className="me-eyebrow">{eyebrow}</div>}
        <h1 className="me-title" title={typeof title === 'string' ? title : undefined}>{title}</h1>
        {metaItems.length > 0 && (
          <div className="me-meta">
            {metaItems.map((entry, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <span key={index} className="me-meta-item">{entry}</span>
            ))}
          </div>
        )}
      </div>
      {(status || actions) && (
        <div className="me-header-side">
          {status}
          {actions}
        </div>
      )}
    </header>
  );
}

/**
 * Two-column body: main column (col-xl-8) and aside (col-xl-4). The split starts
 * at xl, not lg: from lg up the app shows its 240px sidebar, so at 992-1199px a
 * 4/12 aside would only be ~220-300px wide. Below xl the aside is shown first
 * (publishing metadata right after the header), its panels two-up from md
 * (see .me-aside). The gutter is 1rem below md so the negative row margins
 * (-0.5rem) never exceed the app's 0.5rem page padding on phones (main.px-2).
 */
export function EditorColumns({ aside, children, asideLabel }) {
  return (
    <div className="row g-3 g-md-4">
      <div className="col-xl-8 order-2 order-xl-1 me-main">{children}</div>
      {aside && (
        <aside className="col-xl-4 order-1 order-xl-2 me-aside" aria-label={asideLabel}>
          {aside}
        </aside>
      )}
    </div>
  );
}

/** The one main card; its direct EditorSection children are separated by hairlines. */
export function EditorMain({ children, className = '' }) {
  return <div className={`card ${className}`.trim()}>{children}</div>;
}

/**
 * Section of the main card: icon + title + description on the left, `tools`
 * (e.g. LanguageSwitch) on the right, content below. Place directly inside
 * EditorMain (hairline between consecutive sections).
 */
export function EditorSection({ id, icon, title, description, tools, children, className = '' }) {
  const generatedId = useId();
  const titleId = `${id || generatedId}-title`;
  return (
    <section id={id} className={`me-section ${className}`.trim()} aria-labelledby={titleId}>
      <div className="me-section-head">
        <div className="me-section-heading">
          {icon && <span className="me-section-icon" aria-hidden="true">{renderIcon(icon)}</span>}
          <div className="me-section-text">
            <h2 className="me-section-title" id={titleId}>{title}</h2>
            {description && <p className="me-section-desc">{description}</p>}
          </div>
        </div>
        {tools && <div className="me-section-tools">{tools}</div>}
      </div>
      {children}
    </section>
  );
}

/** Labelled group inside a section (small sentence-case subheading). */
export function FieldGroup({ title, children, className = '' }) {
  const titleId = useId();
  return (
    <div className={`me-group ${className}`.trim()} role="group" aria-labelledby={title ? titleId : undefined}>
      {title && <h3 className="me-group-title" id={titleId}>{title}</h3>}
      {children}
    </div>
  );
}

/** Compact card of the aside column. */
export function SidePanel({ id, title, description, children, className = '' }) {
  const titleId = useId();
  return (
    <section id={id} className={`card me-panel ${className}`.trim()} aria-labelledby={title ? titleId : undefined}>
      <div className="card-body p-3">
        {title && <h2 className="me-panel-title" id={titleId}>{title}</h2>}
        {description && <p className="me-panel-desc">{description}</p>}
        <div className={title || description ? 'me-panel-body' : undefined}>{children}</div>
      </div>
    </section>
  );
}

/** Live character count under a text field ("{{count}} characters"). */
export function CharCount({ text, className = '' }) {
  const { t } = useTranslation();
  const count = countChars(text);
  return <div className={`me-count ${className}`.trim()}>{t('{{count}} characters', { count })}</div>;
}

/**
 * Segmented DE / EN switch with flags. A filled dot marks a language that has a
 * title; an invalid language gets a red dot and a "Title missing" screen-reader
 * text (put aria-invalid on the title input itself - buttons do not support it).
 * `filledLabel` overrides the screen-reader text of a filled language (default
 * "Title entered"), e.g. when the dot also counts the teaser / description.
 * @param {{value: 'de'|'en', onChange: (lang: string) => void, filled?: {de?: boolean, en?: boolean}, filledLabel?: string, invalid?: {de?: boolean, en?: boolean}, label?: string}} props
 */
export function LanguageSwitch({ value, onChange, filled = {}, filledLabel, invalid = {}, label }) {
  const { t } = useTranslation();
  return (
    <div className="me-segmented" role="group" aria-label={label || t('Language')}>
      {LANGUAGES.map((lang) => {
        const active = value === lang.code;
        const isInvalid = Boolean(invalid?.[lang.code]);
        const isFilled = Boolean(filled?.[lang.code]);
        let stateText = '';
        if (isInvalid) stateText = t('Title missing');
        else if (isFilled) stateText = filledLabel || t('Title entered');
        return (
          <button
            key={lang.code}
            type="button"
            className="me-segment"
            aria-pressed={active}
            data-invalid={isInvalid || undefined}
            onClick={() => {
              if (!active) onChange(lang.code);
            }}
          >
            <ReactCountryFlag countryCode={lang.flag} svg className="me-flag" alt="" aria-hidden="true" />
            <span aria-hidden="true">{lang.short}</span>
            <span className={`me-lang-dot${isFilled ? ' is-filled' : ''}${isInvalid ? ' is-invalid' : ''}`} aria-hidden="true" />
            <span className="visually-hidden">
              {t(lang.labelKey)}
              {stateText ? `, ${stateText}` : ''}
            </span>
          </button>
        );
      })}
    </div>
  );
}

/**
 * Draft / Published segmented control (published active = success colour,
 * draft active = primary). Full width of its container.
 */
export function StatusToggle({ value, onChange, disabled = false, label }) {
  const { t } = useTranslation();
  const options = [
    { value: 'draft', text: t('Draft'), icon: 'pen' },
    { value: 'published', text: t('Published'), icon: 'globe' },
  ];
  return (
    <div className="me-segmented is-block" role="group" aria-label={label || t('Status')}>
      {options.map((option) => {
        const active = value === option.value;
        return (
          <button
            key={option.value}
            type="button"
            className={`me-segment${option.value === 'published' ? ' is-success' : ''}`}
            aria-pressed={active}
            disabled={disabled}
            onClick={() => {
              if (!active) onChange(option.value);
            }}
          >
            <MDBIcon fas icon={option.icon} aria-hidden="true" />
            {option.text}
          </button>
        );
      })}
    </div>
  );
}

/**
 * Sticky bottom bar: save state on the left, Delete / Back / Save on the right.
 * `labels` overrides (already translated strings): createState, dirty, clean,
 * uploadBusy, errors (function count => string), delete, back, save.
 */
export function StickyActionBar({
  mode = 'edit',
  isDirty = false,
  uploadBusy = false,
  errorCount = 0,
  onErrorsClick,
  onDelete,
  onBack,
  onSave,
  saving = false,
  saveDisabled = false,
  deleteDisabled = false,
  labels = {},
}) {
  const { t } = useTranslation();
  const text = {
    createState: labels.createState || t('New item – not saved yet'),
    dirty: labels.dirty || t('Unsaved changes'),
    clean: labels.clean || t('All changes saved'),
    uploadBusy: labels.uploadBusy || t('Please wait until the upload has finished.'),
    delete: labels.delete || t('Delete'),
    back: labels.back || t('Back'),
    save: labels.save || t('Save'),
  };
  const errorsText = typeof labels.errors === 'function'
    ? labels.errors(errorCount)
    : t('Please fix {{count}} problem(s)', { count: errorCount });

  let state;
  if (uploadBusy) {
    state = (
      <span className="me-state">
        <MDBSpinner size="sm" role="presentation" className="flex-shrink-0" />
        <span>{text.uploadBusy}</span>
      </span>
    );
  } else if (mode === 'create') {
    state = (
      <span className="me-state">
        <span className={`me-dot${isDirty ? '' : ' is-neutral'}`} aria-hidden="true" />
        <span>{text.createState}</span>
      </span>
    );
  } else if (isDirty) {
    state = (
      <span className="me-state">
        <span className="me-dot" aria-hidden="true" />
        <span>{text.dirty}</span>
      </span>
    );
  } else {
    state = (
      <span className="me-state is-clean">
        <MDBIcon fas icon="check" aria-hidden="true" />
        <span>{text.clean}</span>
      </span>
    );
  }

  return (
    <div className="me-actionbar" role="region" aria-label={t('Actions')}>
      <div className="me-actionbar-status">
        <span aria-live="polite">{state}</span>
        {errorCount > 0 && (
          <button type="button" className="me-actionbar-errors" onClick={onErrorsClick}>
            <MDBIcon fas icon="exclamation-circle" className="me-1" aria-hidden="true" />
            {errorsText}
          </button>
        )}
      </div>
      <div className="me-actionbar-actions">
        {mode === 'edit' && onDelete && (
          <MDBBtn color="link" type="button" className="me-actionbar-delete" onClick={onDelete} disabled={saving || deleteDisabled}>
            <MDBIcon fas icon="trash-alt" className="me-2" aria-hidden="true" />
            {text.delete}
          </MDBBtn>
        )}
        {onBack && (
          <MDBBtn color="link" type="button" onClick={onBack}>
            {text.back}
          </MDBBtn>
        )}
        <MDBBtn
          color="primary"
          type="button"
          onClick={onSave}
          disabled={saving || uploadBusy || saveDisabled}
          aria-keyshortcuts="Control+S Meta+S"
        >
          {saving && <MDBSpinner size="sm" className="me-2" role="presentation" />}
          {text.save}
        </MDBBtn>
      </div>
    </div>
  );
}

/**
 * Asks the browser to confirm leaving the page (reload / close / external link)
 * while `enabled` is true. In-app navigation is not affected.
 */
export function useBeforeUnload(enabled) {
  useEffect(() => {
    if (!enabled) return undefined;
    const handler = (event) => {
      event.preventDefault();
      // Chrome/Edge need returnValue to show the dialog
      // eslint-disable-next-line no-param-reassign
      event.returnValue = '';
      return '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [enabled]);
}

/**
 * Dirty tracking of a form value: compares stableStringify(value) with a
 * snapshot that is taken whenever `resetKey` changes (e.g. the hook's loadVersion
 * after a fetch, or a counter bumped together with a copy prefill). Change the
 * key in the same render as the value (same state batch) so no false "dirty"
 * render happens in between.
 * @param {any} value current form state
 * @param {any} resetKey snapshot key
 * @param {{warnOnUnload?: boolean}} [options] warnOnUnload: beforeunload prompt while dirty
 * @returns {{isDirty: boolean, markClean: () => void}}
 */
export function useDirtyState(value, resetKey, { warnOnUnload = false } = {}) {
  const serialized = useMemo(() => stableStringify(value), [value]);
  const [baseline, setBaseline] = useState(() => ({ key: resetKey, snapshot: serialized }));
  const latest = useRef(serialized);
  latest.current = serialized;

  let snapshot = baseline.snapshot;
  if (!Object.is(baseline.key, resetKey)) {
    // Derived state: new snapshot for the new key (React re-renders immediately)
    snapshot = serialized;
    setBaseline({ key: resetKey, snapshot: serialized });
  }

  const markClean = useCallback(() => {
    setBaseline((prev) => ({ key: prev.key, snapshot: latest.current }));
  }, []);

  const isDirty = serialized !== snapshot;
  useBeforeUnload(Boolean(warnOnUnload && isDirty));
  return { isDirty, markClean };
}

/**
 * Ctrl+S / Cmd+S calls `handler` while `enabled`. The browser's "save page"
 * dialog is suppressed while the editor is mounted. Ignored while focus is in an
 * open dialog (MDB modal, e.g. TipTap's HTML / link dialogs, the BilingualEditor
 * fullscreen dialog, confirm modals): their pending input is only applied on
 * confirm and would otherwise be lost by save + navigate.
 */
export function useSaveShortcut(handler, enabled = true) {
  const handlerRef = useRef(handler);
  const enabledRef = useRef(enabled);
  handlerRef.current = handler;
  enabledRef.current = enabled;

  useEffect(() => {
    const onKeyDown = (event) => {
      if (!(event.ctrlKey || event.metaKey) || event.altKey || event.shiftKey) return;
      if (typeof event.key !== 'string' || event.key.toLowerCase() !== 's') return;
      event.preventDefault();
      if (event.repeat || !enabledRef.current || typeof handlerRef.current !== 'function') return;
      if (typeof Element !== 'undefined' && event.target instanceof Element
        && event.target.closest('.modal.show, [role="dialog"]')) return;
      handlerRef.current(event);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);
}
